import { SelectField } from '@almbrand/selectfield';
import { ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import classNames from 'classnames';
import { PageContext } from 'context/PageContext';
import { useModal, useSpdSearch } from 'hooks';
import { useContext, useEffect, useRef, useState } from 'react';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import { Obj } from 'src/hooks/useSpdSearch';
import { useAppDispatch } from 'store/hooks';
import { applyBrandFlowStopField } from '../../../services/brandFlowStop';
import {
	applyExpensiveCarFields,
	clearExpensiveCarFields,
	readExpensiveCarConfig,
	SaveFormValue,
} from '../../../services/expensiveCarCategory';
import {
	clearCarGroupResult,
	persistCarGroupResult,
	runCarGroupLookup,
	SpdParam,
} from '../../../services/VehicleLookupService';
import { Option } from './CarSearch';
import styles from './CarSearch.module.scss';
import { isSearchResultValid } from './validateCarSearch';

type StepKey = 'brand' | 'model' | 'year' | 'variant';
type MakeAndModelState = { name: string; param: SpdParam }[];
type ByStep<T> = Record<StepKey, T>; // Represents the state of the car make and model selection, including the name and associated parameters.
type StepOptions = { errorMessage?: string; options: Option[] };

export interface CarMakeAndModelProps {
	makeSearchField: SpdSearchType;
	yearSearchField: SpdSearchType;
	modelSearchField: SpdSearchType;
	variantSearchField: SpdSearchType;
	themeName?: ThemeUnion;
	saveOutputFieldsToForm: (
		register: UseFormRegister<FieldValues>,
		searchConfiguration: SpdSearchType['searchConfiguration'],
		value: Option | Obj,
		contentType: string,
		completeCarSearch?: boolean
	) => void;
	saveFormValue: SaveFormValue;
	register: UseFormRegister<FieldValues>;
	contentType: string;
	stepContentId: number;
	updateCarSearchIsValid: (isValid: boolean) => void;
}

// Utility function to create a record keyed by each step with the same initial value.
const byStep = <T,>(value: T): ByStep<T> => ({ brand: value, model: value, year: value, variant: value });

export const CarMakeAndModel: React.FC<CarMakeAndModelProps> = ({
	makeSearchField,
	yearSearchField,
	modelSearchField,
	variantSearchField,
	themeName,
	saveOutputFieldsToForm,
	saveFormValue,
	register,
	contentType,
	stepContentId,
	updateCarSearchIsValid,
}) => {
	const { displayErrorModal } = useModal();
	const dispatch = useAppDispatch();
	const pageContext = useContext(PageContext);
	// CMS-defined static defaults (e.g. VEHICLE_TYPE) keyed by uniqueIdentifier.uid
	const staticFieldItems = pageContext?.metaArea?.items?.[0]?.content?.staticFields?.items ?? [];

	const brandSearch = useSpdSearch({ spdSearchType: makeSearchField });
	const modelSearch = useSpdSearch({ spdSearchType: modelSearchField });
	const yearSearch = useSpdSearch({ spdSearchType: yearSearchField });
	const variantSearch = useSpdSearch({ spdSearchType: variantSearchField });

	// Define the steps for the "make/model" car search form, including the key, field, search hook, placeholder, and CSS class.
	const steps: Array<{
		key: StepKey;
		field: SpdSearchType;
		search: ReturnType<typeof useSpdSearch>;
		placeHolder: string;
		className: string;
	}> = [
		{
			key: 'brand',
			field: makeSearchField,
			search: brandSearch,
			placeHolder: 'Bilmærke',
			className: styles.CarSearch__selectFieldBrand,
		},
		{
			key: 'model',
			field: modelSearchField,
			search: modelSearch,
			placeHolder: 'Model',
			className: styles.CarSearch__selectFieldModel,
		},
		{
			key: 'year',
			field: yearSearchField,
			search: yearSearch,
			placeHolder: 'Årgang',
			className: styles.CarSearch__selectFieldYear,
		},
		{
			key: 'variant',
			field: variantSearchField,
			search: variantSearch,
			placeHolder: 'Variant',
			className: styles.CarSearch__selectFieldVariant,
		},
	];

	const [makeAndModelState, setMakeAndModelState] = useState<MakeAndModelState>([]);
	const [options, setOptions] = useState<ByStep<StepOptions>>(byStep({ options: [] }));
	const [loading, setLoading] = useState<ByStep<boolean>>(byStep(false));
	const [remountKey, setRemountKey] = useState<ByStep<number>>(byStep(0));

	// Monotonic request ids: any populate whose id no longer matches on
	// resolve is stale and its result is discarded. Prevents the "variant is
	// empty until I pick another year" race where an earlier in-flight
	// request overwrites fresh options with an empty array.
	const requestIdRef = useRef<ByStep<number>>(byStep(0));
	const detailsRequestIdRef = useRef(0);

	useEffect(() => {
		void populate('brand', []);
		return () => {
			// Bump every request id so any in-flight populate / variant-details resolves as stale
			// and can't leak persistence into the parent form after unmount (e.g. user toggled back to plate mode).
			(Object.keys(requestIdRef.current) as StepKey[]).forEach((k) => (requestIdRef.current[k] += 1));
			detailsRequestIdRef.current += 1;
		};
	}, []);

	const populate = async (key: StepKey, state: MakeAndModelState) => {
		const step = steps.find((s) => s.key === key)!;
		const errorMessage = step.field.errorMessage ?? '';
		const requestId = ++requestIdRef.current[key];
		setLoading((prev) => ({ ...prev, [key]: true }));

		const params = buildParams(step.field.searchConfiguration, state);
		let result: Awaited<ReturnType<typeof step.search.doSearch>> | undefined;
		try {
			result = await step.search.doSearch({
				name: step.field.searchConfiguration.spdSearchConfigurationName,
				params,
			});
		} catch (error) {
			if (requestIdRef.current[key] !== requestId) return;
			console.error(`Populate ${key} failed:`, error);
			setLoading((prev) => ({ ...prev, [key]: false }));
			return;
		}

		if (requestIdRef.current[key] !== requestId) return;
		setLoading((prev) => ({ ...prev, [key]: false }));

		if (Array.isArray(result)) {
			setOptions((prev) => ({
				...prev,
				[key]: { ...prev[key], options: result, errorMessage: errorMessage },
			}));
		}
	};

	// Handle changes to the car search form fields, updating state and triggering downstream effects.
	const handleChange = (index: number, value: Option) => {
		const step = steps[index];
		if (
			step.key === 'brand' &&
			applyBrandFlowStopField({
				brandName: value.label,
				saveFormValue,
				dispatch,
				contentType,
				page: String(stepContentId),
			})
		) {
			return;
		}

		const nextState = computeNextState(makeAndModelState, step.field, value);
		const downstream = steps.slice(index + 1).map((s) => s.key);
		const nextStepKey = steps[index + 1]?.key;

		// Invalidate anything in flight below this step before kicking off the next fetch.
		downstream.forEach((k) => (requestIdRef.current[k] += 1));
		detailsRequestIdRef.current += 1;

		setMakeAndModelState(nextState);
		setOptions((prev) => {
			const next = { ...prev };
			downstream.forEach((k) => (next[k] = { options: [] }));
			return next;
		});
		setRemountKey((prev) => {
			const next = { ...prev };
			downstream.forEach((k) => (next[k] += 1));
			return next;
		});
		setLoading((prev) => {
			const next = { ...prev };
			downstream.forEach((k) => (next[k] = false));
			// Show the spinner synchronously on whichever select we're about
			// to hit, so there's no flash of "empty enabled dropdown".
			next[nextStepKey ?? step.key] = true;
			return next;
		});

		saveOutputFieldsToForm(register, step.field.searchConfiguration, value, contentType);

		if (nextStepKey) {
			void populate(nextStepKey, nextState);
		} else {
			void loadVariantDetails(nextState);
		}
	};

	const loadVariantDetails = async (state: MakeAndModelState) => {
		const requestId = ++detailsRequestIdRef.current;
		const isStale = () => detailsRequestIdRef.current !== requestId;
		// A new variant lookup invalidates the parent car search and any
		// previously stored CAR_GROUP until both variant-details and
		// AGT_CARDTL calls succeed.
		updateCarSearchIsValid(false);
		clearCarGroupResult(dispatch);
		clearExpensiveCarFields(saveFormValue);
		try {
			const params = buildParams(variantSearchField.secondarySearchConfiguration, state);
			const result = await variantSearch.doSearch(
				{ name: 'VEHICLE_VARIANT_DETAILS_CODAN', params },
				variantSearchField.secondarySearchConfiguration
			);
			if (isStale()) return;
			if (!isSearchResultValid(variantSearchField.secondarySearchConfiguration, result as Obj)) {
				console.warn('VARIANT_DETAILS returned an unusable payload', result);
				displayErrorModal(
					'Vi kunne ikke hente bilens oplysninger',
					'',
					'Prøv igen om lidt, eller indtast bilens oplysninger manuelt.'
				);
				return;
			}

			const cardtl = await runCarGroupLookup(variantSearch.url, result as Obj);
			if (isStale()) return;

			persistCarGroupResult({ dispatch, contentType, page: String(stepContentId), cardtl });
			const yearValue = state.find((s) => s.name === yearSearchField.uniqueIdentifier.uid)?.param.param_value;
			applyExpensiveCarFields({
				variantDetails: result as Obj,
				ageInput: { kind: 'modelYear', value: yearValue },
				config: readExpensiveCarConfig(pageContext?.metaArea?.items?.[0]?.content?.staticFields),
				saveFormValue,
			});
			saveOutputFieldsToForm(
				register,
				variantSearchField.secondarySearchConfiguration,
				result as Obj,
				contentType,
				true
			);
		} catch (error) {
			if (isStale()) return;
			console.error('Car group lookup failed:', error);
			displayErrorModal(
				'Vi kunne ikke hente bilens oplysninger',
				'',
				'Prøv igen om lidt, eller indtast bilens oplysninger manuelt.'
			);
		} finally {
			if (!isStale()) setLoading((prev) => ({ ...prev, variant: false }));
		}
	};

	const buildParams = (searchConfiguration: SpdSearchType['searchConfiguration'], state: MakeAndModelState) =>
		searchConfiguration.inputFields.items.map((item) => {
			const uid = item.content.uniqueIdentifier.uid;
			const found = state.find((s) => s.name === uid);
			if (found) return found.param;

			const staticDefault = staticFieldItems.find(
				(staticItem) => staticItem?.content?.uniqueIdentifier?.uid === uid
			)?.content?.defaultValue;

			return { param_name: item.content.param, param_value: staticDefault ?? '' };
		});

	// If the field was already picked, drop everything downstream so the chain restarts from this point.
	const computeNextState = (state: MakeAndModelState, field: SpdSearchType, value: Option): MakeAndModelState => {
		const existingIndex = state.findIndex((item) => item.name === field.uniqueIdentifier.uid);
		const outputField = field.searchConfiguration.outputFields.items;

		if (existingIndex !== -1) {
			return state
				.slice(0, existingIndex + 1)
				.map((item, i) =>
					i === existingIndex ? { ...item, param: { ...item.param, param_value: value.value } } : item
				);
		}

		return [
			...state,
			...outputField
				.filter((output) => !state.some((item) => item.name === output.content.uniqueIdentifier.uid))
				.map((output) => ({
					name: output.content.uniqueIdentifier.uid,
					param: { param_name: output.content.param, param_value: value.value },
				})),
		];
	};

	return (
		<>
			{steps.map((step, i) => {
				const prev = steps[i - 1];
				const isDisabled = prev
					? !makeAndModelState.some((s) => s.name === prev.field.uniqueIdentifier.uid)
					: false;
				return (
					<div
						key={step.key}
						className={classNames(styles.CarSearch__selectField, step.className)}
					>
						<SelectField
							themeName={themeName}
							key={`${step.key}-${remountKey[step.key]}`}
							options={options[step.key].options}
							isDisabled={isDisabled}
							errorMessage={options[step.key].errorMessage}
							icon={{ isIcon: false }}
							placeHolder={step.placeHolder}
							isSearchable
							iconPosition='right'
							onChange={(value: Option) => handleChange(i, value)}
							isLoading={loading[step.key]}
						/>
					</div>
				);
			})}
		</>
	);
};
